/* Owns every value → display mapping used more than once: dates, durations,
   bytes, numbers, and the severity / action / status → colour-class + label-key
   tables of PANEL.md §9. Components read the tables; none of them invent a
   colour or a label of their own. */

import type { Action, CommandStatus, GlobalStatus, Locale, ServerStatus, Severity } from '@sentinelac/shared'

// ────────────────────────────── Intl formatting ───────────────────────────────

const dateTimeCache = new Map<string, Intl.DateTimeFormat>()

function dateTimeFormat(locale: Locale, timeZone: string | undefined): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone ?? 'local'}`
  const cached = dateTimeCache.get(key)
  if (cached) return cached
  // Constructing an Intl formatter is expensive enough that a table rendering
  // 200 timestamps notices it; the cache key is the whole option set.
  const formatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'medium',
    ...(timeZone ? { timeZone } : {}),
  })
  dateTimeCache.set(key, formatter)
  return formatter
}

export function formatDateTime(value: string | null | undefined, locale: Locale, timeZone?: string): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  try {
    return dateTimeFormat(locale, timeZone).format(date)
  } catch {
    // An invalid `server.timezone` must not break a whole table.
    return dateTimeFormat(locale, undefined).format(date)
  }
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
  ['second', 1],
]

export function formatRelative(value: string | null | undefined, locale: Locale, now = Date.now()): string {
  if (!value) return '—'
  const time = new Date(value).getTime()
  if (Number.isNaN(time)) return '—'
  const deltaSec = Math.round((time - now) / 1000)
  const abs = Math.abs(deltaSec)
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  for (const [unit, seconds] of RELATIVE_UNITS) {
    if (abs >= seconds || unit === 'second') {
      return formatter.format(Math.round(deltaSec / seconds), unit)
    }
  }
  return formatter.format(0, 'second')
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale).format(value)
}

/** Compact `2s 14dk` / `2h 14m` style used by playtime, uptime and heartbeat age. */
export function formatDuration(totalSeconds: number | null | undefined, locale: Locale): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) return '—'
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const units =
    locale === 'tr'
      ? { d: 'g', h: 's', m: 'dk', s: 'sn' }
      : { d: 'd', h: 'h', m: 'm', s: 's' }
  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3_600)
  const minutes = Math.floor((seconds % 3_600) / 60)
  const rest = seconds % 60
  if (days > 0) return `${days}${units.d} ${hours}${units.h}`
  if (hours > 0) return `${hours}${units.h} ${minutes}${units.m}`
  if (minutes > 0) return `${minutes}${units.m} ${rest}${units.s}`
  return `${rest}${units.s}`
}

export function formatBytes(bytes: number | null | undefined, locale: Locale): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  const decimals = unitIndex === 0 ? 0 : 1
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: decimals }).format(value)} ${units[unitIndex]}`
}

export function truncateMiddle(value: string, keep = 10): string {
  if (value.length <= keep) return value
  const head = Math.ceil(keep / 2)
  const tail = Math.floor(keep / 2)
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`
}

// ──────────────────── semantic value → token + label mappings ─────────────────
// The three tables PANEL.md §9 calls out, used identically everywhere. Class
// names are written out in full because Tailwind scans source text and cannot
// see a name assembled at runtime.

export interface ToneClasses {
  /** Solid fill, used by dots and filled badges. */
  bg: string
  text: string
  border: string
}

export const SEVERITY_CLASSES: Record<Severity, ToneClasses> = {
  low: { bg: 'bg-sev-low', text: 'text-sev-low', border: 'border-sev-low' },
  medium: { bg: 'bg-sev-medium', text: 'text-sev-medium', border: 'border-sev-medium' },
  high: { bg: 'bg-sev-high', text: 'text-sev-high', border: 'border-sev-high' },
  critical: { bg: 'bg-sev-critical', text: 'text-sev-critical', border: 'border-sev-critical' },
}

export const ACTION_CLASSES: Record<Action, ToneClasses> = {
  log: { bg: 'bg-text-faint', text: 'text-text-muted', border: 'border-border-strong' },
  kick: { bg: 'bg-warn', text: 'text-warn', border: 'border-warn' },
  ban: { bg: 'bg-danger', text: 'text-danger', border: 'border-danger' },
}

export const SERVER_STATUS_CLASSES: Record<ServerStatus, ToneClasses> = {
  never: { bg: 'bg-text-faint', text: 'text-text-faint', border: 'border-border' },
  online: { bg: 'bg-ok', text: 'text-ok', border: 'border-ok' },
  offline: { bg: 'bg-danger', text: 'text-danger', border: 'border-danger' },
}

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'

export const COMMAND_STATUS_TONE: Record<CommandStatus, BadgeTone> = {
  queued: 'neutral',
  delivered: 'accent',
  acked: 'ok',
  failed: 'danger',
  expired: 'danger',
}

export const GLOBAL_STATUS_TONE: Record<GlobalStatus, BadgeTone> = {
  none: 'neutral',
  proposed: 'warn',
  approved: 'ok',
  rejected: 'danger',
  revoked: 'danger',
}

export const severityLabelKey = (severity: Severity): string => `common.severity.${severity}`
export const actionLabelKey = (action: Action): string => `common.action.${action}`
export const serverStatusLabelKey = (status: ServerStatus): string => `common.status.${status}`
export const commandStatusLabelKey = (status: CommandStatus): string => `command.status.${status}`
export const globalStatusLabelKey = (status: GlobalStatus): string => `ban.global.${status}`
export const roleLabelKey = (role: string): string => `common.role.${role}`

/** Trust score colour band: red below 30, amber below 60, green above (PANEL.md §10). */
export function trustTone(score: number): ToneClasses {
  if (score < 30) return { bg: 'bg-danger', text: 'text-danger', border: 'border-danger' }
  if (score < 60) return { bg: 'bg-warn', text: 'text-warn', border: 'border-warn' }
  return { bg: 'bg-ok', text: 'text-ok', border: 'border-ok' }
}

/**
 * Turkish casing is not the invariant casing: `i` uppercases to `İ`. Applied
 * only to prose, never to identifiers, ban codes or `DetectionKey`s.
 */
export function localeUpper(value: string, locale: Locale): string {
  return locale === 'tr' ? value.toLocaleUpperCase('tr') : value.toLocaleUpperCase(locale)
}
